<script setup>
  import { usePage, Head } from '@inertiajs/vue3';
  import { computed, onMounted, onBeforeUnmount } from 'vue';

  // Accept custom meta as props if needed
  const props = defineProps({
    title: {
      type: String,
      default: null,
    },
    description: {
      type: String,
      default: null,
    },
    keywords: {
      type: String,
      default: null,
    },
    canonical: {
      type: String,
      default: null,
    },
    image: {
      type: String,
      default: null,
    },
    breadcrumbs: {
      type: Array,
      default: () => [],
    },
    pageType: {
      type: String,
      default: 'website',
    },
    robots: {
      type: String,
      default: 'index, follow', // Default to allow indexing
    },
    faqs: {
      type: Array,
      default: () => [],
    },
    sections: {
      type: Array,
      default: () => [],
    },
    product: {
      type: Object,
      default: null,
    }
  });

  // Access the page prop to get meta data from Inertia
  const page = usePage();

  // Compute meta values from props or fall back to page props
  const metaTitle = computed(() => props.title || page.props.meta?.title || 'Default Title');
  const metaDescription = computed(
    () => props.description || page.props.meta?.description || 'Default Description'
  );
  const metaKeywords = computed(
    () => props.keywords || page.props.meta?.keywords || 'default, keywords'
  );

  const metaCanonical = computed(() => {
    if (props.canonical) return props.canonical.startsWith('http') ? props.canonical : `https://www.balan.ai${props.canonical}`;
    if (page.props.meta?.canonical) return page.props.meta.canonical.startsWith('http') ? page.props.meta.canonical : `https://www.balan.ai${page.props.meta.canonical}`;
    return window.location.href;
  });

  const metaImage = computed(() => {
    const defaultImage = 'https://www.balan.ai/logo-balan.png';
    if (props.image) return props.image.startsWith('http') ? props.image : `https://www.balan.ai${props.image}`;
    if (page.props.meta?.image) return page.props.meta.image.startsWith('http') ? page.props.meta.image : `https://www.balan.ai${page.props.meta.image}`;
    return defaultImage;
  });

  const metaRobots = computed(() => props.robots || 'index, follow');

  const organizationSchema = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://www.balan.ai/#organization',
    name: 'Balan.ai',
    url: 'https://www.balan.ai',
    logo: {
      '@type': 'ImageObject',
      url: 'https://www.balan.ai/logo-balan.png'
    },
    description: 'Balan.ai provides a curated AI tools list and AI applications directory to help you find the right AI solutions for your needs.',
    sameAs: [
      'https://twitter.com/BalanEusebiu',
      'https://www.threads.net/@eusebiu.ba'
    ]
  }));

  // Structured data schema based on page type
  const structuredData = computed(() => {
    const baseSchema = {
      '@context': 'https://schema.org',
      '@id': metaCanonical.value,
      headline: metaTitle.value,
      description: metaDescription.value,
      image: metaImage.value,
      url: metaCanonical.value,
      mainEntityOfPage: metaCanonical.value,
      author: {
        '@type': 'Organization',
        name: 'Balan.ai',
        url: 'https://www.balan.ai',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Balan.ai',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.balan.ai/logo-balan.png', // Update with your actual logo URL
        },
      },
      datePublished: page.props.post?.publish_date || new Date().toISOString(),
      dateModified: new Date().toISOString(),
    };

    // Set @type based on pageType
    switch (props.pageType) {
      case 'article':
        baseSchema['@type'] = 'Article';
        break;
      case 'blog':
        baseSchema['@type'] = 'Blog';
        // Add blog-specific properties with safety checks
        if (page.props.posts) {
          // Check if posts is an object with a data property (Laravel pagination)
          const postsData = Array.isArray(page.props.posts) 
            ? page.props.posts 
            : (page.props.posts.data || []);

          baseSchema.blogPost = postsData.map(post => ({
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.excerpt,
            url: `https://www.balan.ai/blog/${post.slug}`
          }));
        }
        break;
        case 'tool':
          baseSchema['@type'] = 'SoftwareApplication';
          if (props.product) {
            // Create a modified product without localhost references
            const safeProduct = { ...props.product };
            
            // Ensure image paths are relative or absolute without localhost
            if (safeProduct.image && safeProduct.image.includes('localhost')) {
              safeProduct.image = new URL(safeProduct.image).pathname;
            }
            
            // Ensure URL is consistent with canonical
            safeProduct.url = metaCanonical.value;
            safeProduct.mainEntityOfPage = metaCanonical.value;
            
            // Add FAQs to the structured data if they exist
            if (props.faqs && Array.isArray(props.faqs) && props.faqs.length > 0) {
              safeProduct.potentialAction = {
                '@type': 'AskAction',
                question: props.faqs.map(faq => ({
                  '@type': 'Question',
                  name: faq.question,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer
                  }
                }))
              };
            }
            
            return {
              ...baseSchema,
              ...safeProduct,
              '@context': 'https://schema.org'
            };
          }
          break;
      case 'about':
        baseSchema['@type'] = 'AboutPage';
        break;
      case 'faq':
        if (Array.isArray(props.faqs) && props.faqs.length > 0) {
          baseSchema['@type'] = 'FAQPage';
          baseSchema.mainEntity = props.faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
              author: {
                '@type': 'Organization',
                name: 'Balan.ai'
              },
              datePublished: new Date().toISOString()
            },
          }));
        }
        break;
      case 'guide':
        baseSchema['@type'] = 'HowTo';
        baseSchema.step = props.sections.map((section, index) => ({
          '@type': 'HowToStep',
          position: index + 1,
          name: section.title,
          text: section.content,
          url: `${metaCanonical.value}#step-${index + 1}`
        }));
        // Additional HowTo-specific properties
        baseSchema.totalTime = 'PT10M'; // Estimated time to complete all steps
        baseSchema.supply = [
          {
            '@type': 'HowToSupply',
            name: 'Web browser'
          }
        ];
        baseSchema.tool = [
          {
            '@type': 'HowToTool',
            name: 'Internet connection'
          }
        ];
        break;
      default:
        baseSchema['@type'] = 'WebPage';
    }

    return baseSchema;
  });

  // Breadcrumb structured data
  const breadcrumbSchema = computed(() => {
    if (!props.breadcrumbs || props.breadcrumbs.length === 0) return null;

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: props.breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.label,
        item: crumb.url ? (crumb.url.startsWith('http') 
          ? crumb.url 
          : `https://www.balan.ai${crumb.url.startsWith('/') ? crumb.url : `/${crumb.url}`}`) 
        : metaCanonical.value
      })),
    };
  });

  // Utility function to remove old scripts
  const removeOldScripts = () => {
    document.querySelectorAll('script[type="application/ld+json"]').forEach((script) => {
      script.remove();
    });
  };

  // Inject the structured data using a lifecycle hook
  onMounted(() => {
    // Remove existing ld+json scripts to prevent duplication
    removeOldScripts();

    if (structuredData.value) {
      const scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      scriptTag.text = JSON.stringify(structuredData.value);
      document.head.appendChild(scriptTag);
    }

    if (breadcrumbSchema.value) {
      const breadcrumbScriptTag = document.createElement('script');
      breadcrumbScriptTag.type = 'application/ld+json';
      breadcrumbScriptTag.text = JSON.stringify(breadcrumbSchema.value);
      document.head.appendChild(breadcrumbScriptTag);
    }

    // Add organization schema
    const orgScriptTag = document.createElement('script');
    orgScriptTag.type = 'application/ld+json';
    orgScriptTag.text = JSON.stringify(organizationSchema.value);
    document.head.appendChild(orgScriptTag);
  });

  // Clean up on unmount to ensure no lingering scripts
  onBeforeUnmount(() => {
    removeOldScripts();
  });
</script>

<template>
  <Head>
    <!-- Basic SEO Tags -->
    <title>{{ metaTitle }}</title>
    <meta name="description" :content="metaDescription" />
    <meta name="keywords" :content="metaKeywords" />
    <link rel="canonical" :href="metaCanonical" />
    <meta name="robots" :content="metaRobots" />
    <meta v-if="pageType === 'tool' && product?.datePublished" property="article:published_time" :content="product.datePublished" />
    <meta v-if="pageType === 'tool' && product?.dateModified"  property="article:modified_time"  :content="product.dateModified" />

    <!-- Open Graph Tags -->
    <meta property="og:title" :content="metaTitle" />
    <meta property="og:description" :content="metaDescription" />
    <meta property="og:image" :content="metaImage" />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="630" />
    <meta property="og:url" :content="metaCanonical" />
    <meta property="og:locale" content="en_US" />
    <meta property="og:site_name" content="Balan.ai" />
    <meta property="og:type" :content="['article', 'blog', 'faq', 'tool', 'about'].includes(props.pageType) ? props.pageType === 'blog' ? 'article' : props.pageType : 'website'"/>

    <!-- Twitter Cards -->
    <meta name="twitter:site" content="@BalanEusebiu" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" :content="metaTitle" />
    <meta name="twitter:description" :content="metaDescription" />
    <meta name="twitter:image" :content="metaImage" />
    <meta name="twitter:image:alt" content="Balan.ai Logo" />
  </Head>
</template>